import React, { CSSProperties, useMemo, useCallback } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { IconSuicide, IconSuicideWhite, IconSybil, IconSybilWhite } from '@/assets';
import { IconDefaultAvatar, IconDefaultAvatarDark } from 'assets/icon/avatar';
import { IconContract, IconContractWhite } from 'assets/swap';
import { useSwitch, useTheme, useUserInfo } from '@/hooks';
import {
  getSpecialAvatarUrlByAddress,
  isOfficalAddress,
  isSybilByUserDesc,
  isCexAddress,
  openNewWindow,
  getOfficialPath,
} from '@/utils';
import {
  BotAvatar,
  GroupAvatar,
  HelperTooltip,
  UserCardPopup,
  UserCardPopupPropsPartial,
} from '@/components';
import { OfficialAccountAvatar } from '../OfficialAccountAvatar';
import './index.scss';
import { TooltipAlignConfig, TooltipPlacement } from 'antd/es/tooltip';

export type UserAvatarProps = {
  data?: UserDesc;
  size?: number;
  userCardPopup?: UserCardPopupPropsPartial;
  className?: string;
  onClick?(e: React.MouseEvent<HTMLDivElement>): void;
  avatarPlaceholder?: string;
  isDark?: boolean;
  enableOfficialLogo?: boolean;
  enableGroupAvatar?: boolean;
  officialLogoOption?: {
    size?: number;
    customStyle?: CSSProperties;
    placement?: TooltipPlacement;
    align?: TooltipAlignConfig;
    tooltipClassName?: string;
  };
  groupLogoOption?: {
    size?: number;
  };
  style?: React.CSSProperties;
};

export const UserAvatarWithAddress = ({
  address,
  ...props
}: UserAvatarProps & { address: string }) => {
  const { data } = useUserInfo(address);
  return <UserAvatar data={data?.desc} {...props} />;
};

export const UnionAvatar = (
  props: {
    data?: UnionAccount;
    isBot?: boolean;
    isBotActive?: boolean;
  } & Pick<
    UserAvatarProps,
    | 'size'
    | 'className'
    | 'onClick'
    | 'userCardPopup'
    | 'enableOfficialLogo'
    | 'enableGroupAvatar'
    | 'officialLogoOption'
  >,
) => {
  const { data, isBot, isBotActive, officialLogoOption, ...rest } = props;
  const isOfficalAccount = useMemo(() => isOfficalAddress(data?.id), [data?.id]);

  const officalAccountLogoOption = useMemo(() => {
    return rest.size
      ? {
          ...(officialLogoOption || {}),
          size: rest.size ? rest.size * 0.36 : 28,
        }
      : officialLogoOption;
  }, [officialLogoOption, rest.size]);

  return isBot ? (
    <BotAvatar userDesc={(data as GeneralAccount)?.desc} {...rest} isOffline={!isBotActive} />
  ) : isOfficalAccount ? (
    <OfficialAccountAvatar
      data={data as OfficialAccount}
      {...rest}
      logoOption={officalAccountLogoOption}
    />
  ) : (
    <UserAvatar data={(data as GeneralAccount)?.desc} {...rest} />
  );
};

export const UserAvatar = ({
  data,
  className,
  size,
  onClick,
  avatarPlaceholder,
  userCardPopup,
  isDark,
  officialLogoOption,
  groupLogoOption,
  enableOfficialLogo = true,
  enableGroupAvatar = true,
  style,
}: UserAvatarProps) => {
  const { t } = useTranslation();
  const { on, turnOn } = useSwitch();
  const { isDark: isDarkMode } = useTheme();

  const defaultAvatar = useMemo(
    () => (avatarPlaceholder || isDarkMode ? IconDefaultAvatarDark : IconDefaultAvatar),
    [avatarPlaceholder, isDarkMode],
  );
  const suicideAvatar = useMemo(() => (isDarkMode ? IconSuicideWhite : IconSuicide), [isDarkMode]);
  const defaultSybil = useMemo(() => (isDark ? IconSybilWhite : IconSybil), [isDark]);
  const defaultContract = useMemo(() => (isDark ? IconContractWhite : IconContract), [isDark]);
  const isContract = useMemo(
    () =>
      Object.values(data?.protocol || {}).length + Object.values(data?.contract || {}).length > 0,
    [data?.protocol, data?.contract],
  );

  const isCex = useMemo(() => isCexAddress(data), [data]);

  const logo = useMemo(() => {
    if (data?.user?.is_suicide) {
      return suicideAvatar;
    }
    if (isSybilByUserDesc(data)) {
      return defaultSybil;
    }
    if (getSpecialAvatarUrlByAddress(data?.id)) {
      return getSpecialAvatarUrlByAddress(data?.id);
    }
    if (data?.user?.logo_thumbnail_url || data?.user?.logo_url) {
      return data?.user?.logo_thumbnail_url || data?.user?.logo_url;
    }
    const multisigContract = Object.values(data?.contract || {}).find(item => item.multisig);
    if (multisigContract?.multisig?.logo_url) {
      return multisigContract.multisig?.logo_url;
    }
    if (isCex && data?.cex?.logo_url) {
      return data.cex.logo_url;
    }
    if (data?.protocol) {
      const protocol = Object.values(data?.protocol).find(item => item?.logo_url);
      if (protocol?.logo_url) {
        return protocol.logo_url;
      }
    }
    if (data?.contract) {
      let _logo: string | null = null;
      Object.values(data.contract).forEach(chain => {
        Object.values(chain).forEach((item: any) => {
          if (_logo) {
            return;
          }
          _logo = item?.logo_url;
        });
      });

      if (_logo) {
        return _logo;
      }
    }

    if (isContract) {
      return defaultContract;
    }

    return defaultAvatar;
  }, [data, isCex, isContract, defaultAvatar, suicideAvatar, defaultSybil, defaultContract]);

  const innerIcon = useMemo(
    () => [defaultContract, defaultAvatar, defaultSybil, suicideAvatar].includes(logo),
    [defaultContract, defaultAvatar, defaultSybil, suicideAvatar, logo],
  );

  const displayBothLogo = useMemo(
    () => enableGroupAvatar && data?.user?.group && enableOfficialLogo && data?.user?.official,
    [data?.user?.group, data?.user?.official, enableGroupAvatar, enableOfficialLogo],
  );

  const displayLogoSameSize = useMemo(() => (size || 28) * 0.35, [size]);

  const officialMemberAvatarSize = useMemo(() => {
    if (displayBothLogo) {
      return displayLogoSameSize;
    }
    if (officialLogoOption?.size) {
      return officialLogoOption?.size;
    } else if (size) {
      return Math.round(size * 0.45);
    } else {
      return 24;
    }
  }, [displayBothLogo, displayLogoSameSize, officialLogoOption?.size, size]);

  const groupMemberAvatarSize = useMemo(() => {
    if (displayBothLogo) {
      return displayLogoSameSize;
    }

    if (groupLogoOption?.size) {
      return groupLogoOption?.size;
    } else if (size) {
      return Math.round(size * 0.45);
    } else {
      return 12;
    }
  }, [displayBothLogo, displayLogoSameSize, groupLogoOption?.size, size]);

  const openOfficialProfileLink = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();
      openNewWindow(getOfficialPath(data?.user?.official!), '_blank');
    },
    [data?.user?.official],
  );
  const openGroupDetailLink = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();
      openNewWindow(`/group/${data?.user?.group?.id}`, '_blank');
    },
    [data?.user?.group?.id],
  );

  const sizeStyle = useMemo(() => ({ width: size, height: size }), [size]);
  const _style = useMemo(
    () => ({
      ...sizeStyle,
      ...style,
    }),
    [sizeStyle, style],
  );

  const Avatar = (
    <div className={clsx('db-user-avatar-container', onClick && 'is-clickable')} onClick={onClick}>
      {on ? (
        <img
          src={isContract ? defaultContract : defaultAvatar}
          className={clsx('db-user-avatar', 'is-inner-logo')}
          style={sizeStyle}
          alt=""
        />
      ) : (
        <img
          src={logo}
          className={clsx(
            'db-user-avatar',
            !innerIcon && data?.user?.logo_is_nft && 'is-nft',
            innerIcon && 'is-inner-logo',
          )}
          onError={turnOn}
          style={sizeStyle}
          alt=""
        />
      )}
    </div>
  );

  return (
    <div className={clsx('db-user-avatar-container-wrapper', className)} style={_style}>
      {userCardPopup && data?.id ? (
        <UserCardPopup {...(userCardPopup === true ? {} : userCardPopup)} address={data?.id}>
          {Avatar}
        </UserCardPopup>
      ) : (
        Avatar
      )}

      <div
        className={clsx(
          'db-logo-avatar-list-container',
          displayBothLogo && 'db-logo-avatar-list-container-both',
        )}>
        {!data?.user?.is_suicide ? (
          <>
            {!!data?.user?.official && enableOfficialLogo && (
              <HelperTooltip
                placement="bottom"
                title={t('AvatarEditable.team', { name: data?.user?.official?.name })}>
                <div style={officialLogoOption?.customStyle}>
                  <OfficialAccountAvatar
                    logoVisible={false}
                    size={officialMemberAvatarSize}
                    data={data?.user?.official}
                    onClick={openOfficialProfileLink}
                  />
                </div>
              </HelperTooltip>
            )}

            {!!data?.user?.group && enableGroupAvatar && (
              <HelperTooltip placement="bottom" title={data?.user?.group?.name}>
                <div style={officialLogoOption?.customStyle}>
                  <GroupAvatar
                    logoVisible={false}
                    size={groupMemberAvatarSize}
                    data={data?.user?.group}
                    onClick={openGroupDetailLink}
                  />
                </div>
              </HelperTooltip>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};
